<script setup lang="ts">
import { AccountProtectionAccordion } from '@/types/UserLimits';

// Tela "Dados da conta" do Minha Conta V2 (Figma mobile 19771:42011 /
// desktop 19771:52312). Dados reais dos mesmos stores/composables da página
// antiga (pages/user/account.vue).
const {
  fetchUserProfile,
  userName,
  userEmail,
  userPhone,
  userPhoneDdi,
  userDocument,
  userBirthdate,
  userCountry,
  userCurrency,
  userLanguage,
  userAddressFullAddress,
  userAddressCity,
  userAddressState,
  userAddressZipcode,
  userNationalities,
} = useUserData();

const {
  isEmailVerified,
  isPhoneVerifiedOrComplete,
  isAddressComplete,
  isDocumentComplete,
  isVerifiedToWithdraw,
} = useUserValidations();

const { getMinValueToTriggerModule, isModuleNeededOnFirstWithdraw } =
  useModulesValidation();

const { showMoneyFormat } = useWallet();

// flags de paridade com pages/user/account.vue
const {
  isContractsSectionEnabled,
  isWithinMigrationPeriod,
  userMigrationNewDomain,
  fetchEmailUpdateFeatureFlag,
} = useFeatureFlagsNew();

const { isBankAccountEnabled } = useFeatureFlagsJson();
const isEmailUpdateEnabled = ref<boolean>(false);

const { pixKey } = storeToRefs(useUserDataStore());
const { getUserPixKey } = useUserDataStore();

const { goToLocaleRoute } = useRoutes();
const { forceOpenAccordion } = useAccountProtection();

// ---- Seus dados -----------------------------------------------------------

const phoneDisplay = computed<string>(() => {
  if (!userPhone.value) return 'Não informado';

  // o ddi pode vir com ou sem '+' — normaliza pra não gerar "++55"
  const ddi = String(userPhoneDdi.value || '').replace(/^\+/, '');

  return `+${ddi}${userPhone.value}`;
});

const addressDisplay = computed<string>(() => {
  const cityState = [userAddressCity.value, userAddressState.value]
    .filter(Boolean)
    .join('/');

  const parts = [
    userAddressFullAddress.value,
    cityState,
    userAddressZipcode.value,
  ].filter(Boolean);

  return parts.length ? parts.join(', ') : 'Não informado';
});

// userInfo.nationalities chega como string JSON de objetos {id, value} (ex.:
// '[{"id":1,"value":"Brasileiro (a)"}]') — normalizeStringJsonArray só faz o
// parse, não extrai o "value"; pode vir mais de uma nacionalidade, por isso
// vira lista (uma por linha) em vez de string única.
const userNationalitiesList = computed<string[]>(() => {
  const names = userNationalities.value
    .map((nationality: { value?: string }) => nationality?.value)
    .filter(Boolean);

  return names.length ? names : ['Não informado'];
});

// Mais de 3 nacionalidades: mostra só as 3 primeiras + botão "Ver mais".
const NATIONALITIES_COLLAPSED_LIMIT = 3;
const showAllNationalities = ref(false);

const visibleNationalitiesList = computed<string[]>(() => {
  if (showAllNationalities.value) return userNationalitiesList.value;

  return userNationalitiesList.value.slice(0, NATIONALITIES_COLLAPSED_LIMIT);
});

// ---- Documentos ------------------------------------------------------------

function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length !== 11) return value;

  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function formatBirthdate(value: string): string {
  const [datePart] = value.split('T');
  const [year, month, day] = (datePart || '').split('-');

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

const documentDisplay = computed<string>(() => {
  if (!userDocument.value) return 'Não informado';

  const birth = userBirthdate.value
    ? ` - ${formatBirthdate(userBirthdate.value)}`
    : '';

  return `${formatCpf(userDocument.value)}${birth}`;
});

const PIX_TYPE_LABELS: Record<string, string> = {
  CPF: 'CPF',
  EMAIL: 'E-mail',
  PHONE: 'Celular',
  RANDOM: 'Aleatória',
};

const pixDisplay = computed<string>(() => {
  if (!pixKey.value?.value) return 'Nenhuma chave cadastrada';

  const label = PIX_TYPE_LABELS[pixKey.value.type] || pixKey.value.type;

  return `${label}: ${pixKey.value.value}`;
});

const accountInfoDisplay = computed<string>(() => {
  return [
    userCountry.value,
    userCurrency.value,
    (userLanguage.value || '').toUpperCase(),
  ]
    .filter(Boolean)
    .join(' | ');
});

// ---- Edição embutida --------------------------------------------------------
// Os fluxos de edição (celular, endereço, pix, dados da conta, contratos)
// não têm tela própria no redesign: ao clicar no editar, o FieldCard do campo
// é trocado pelo componente da página antiga (components/user/account/*),
// que já contém exibição + formulário + validação + chamada de API.
type EditableField =
  | 'email'
  | 'phone'
  | 'address'
  | 'pix'
  | 'clabe'
  | 'bank-accounts'
  | 'infos'
  | 'contracts'
  | 'document';

const editingField = ref<EditableField | ''>('');

// só um editor é renderizado por vez, então um ref único basta; todos os
// componentes antigos expõem { isEditing, toggleEdit } via defineExpose
const editorRef = ref<{ isEditing: boolean; toggleEdit: () => void } | null>(
  null,
);

async function openEditor(field: EditableField) {
  if (editingField.value && editingField.value !== field) {
    // já existe um editor aberto: cancela pelo próprio toggleEdit (reseta o
    // estado interno dele) e deixa o watcher abaixo fechar antes de trocar
    editorRef.value?.toggleEdit?.();
    await nextTick();
  }

  editingField.value = field;

  // os componentes antigos montam colapsados: abre a edição programaticamente
  await nextTick();

  if (editorRef.value && !editorRef.value.isEditing) {
    editorRef.value.toggleEdit?.();
  }
}

function closeEditor() {
  if (editingField.value === 'pix') {
    // garante que o FieldCard "Meu Pix" mostre a chave recém-salva
    getUserPixKey();
  }

  editingField.value = '';
}

// quando o componente antigo sai do modo edição (cancelar ou salvar com
// sucesso), volta pro FieldCard v2. O guard old === true evita fechar no
// frame em que o editor monta ainda com isEditing = false.
watch(
  () => editorRef.value?.isEditing,
  (value, oldValue) => {
    if (oldValue === true && value === false) {
      closeEditor();
    }
  },
);

// ---- Card de verificações obrigatórias (flags reais de useUserValidations) -

const withdrawMinValue = computed<string>(() => {
  return showMoneyFormat({
    value: getMinValueToTriggerModule('first_withdraw') / 100,
  });
});

// só mostra as checagens que o módulo first_withdraw realmente exige
// (paridade com WithdrawAlert.vue, que itera getValidationModule)
const withdrawChecks = computed<{ label: string; done: boolean }[]>(() => {
  const checks: { label: string; done: boolean }[] = [];

  if (isModuleNeededOnFirstWithdraw('email')) {
    checks.push({ label: 'E-mail', done: isEmailVerified.value });
  }

  if (isModuleNeededOnFirstWithdraw('phone')) {
    checks.push({ label: 'Celular', done: isPhoneVerifiedOrComplete.value });
  }

  if (isModuleNeededOnFirstWithdraw('address')) {
    checks.push({ label: 'Endereço', done: isAddressComplete.value });
  }

  if (isModuleNeededOnFirstWithdraw('kyc')) {
    checks.push({ label: 'KYC', done: isDocumentComplete.value });
  }

  return checks;
});

// ---- Zona de risco (mesmo fluxo de exclusão da página antiga) --------------

function onDeleteAccount() {
  forceOpenAccordion(AccountProtectionAccordion.Exclusion);
  goToLocaleRoute({
    name: 'user-account-protection',
    query: { section: 'exclusion' },
  });
}

onBeforeMount(() => {
  if (!userEmail.value) {
    fetchUserProfile({ force: true });
  }
});

onMounted(async () => {
  getUserPixKey();
  isEmailUpdateEnabled.value = await fetchEmailUpdateFeatureFlag();
});
</script>

<template>
  <!-- Desktop: painel único com borda englobando tudo (frame 19771:52312);
         mobile: cada seção tem a própria borda -->
  <div
    class="border-solid flex flex-col gap-[12px] md:gap-[10px] md:rounded-[16px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <!-- Banner de migração de domínio (paridade com a página antiga) -->
    <div
      v-if="isWithinMigrationPeriod"
      class="rounded-[12px] bg-v2-success/10 p-[12px] text-[12px] leading-[16px] text-v2-success"
    >
      <span
        v-html="
          $t('account.migration.alert_description', {
            newDomain: userMigrationNewDomain,
          })
        "
      />
    </div>

    <!-- Seus dados -->
    <section
      class="border-solid flex flex-col gap-[10px] rounded-[16px] border border-v2-default/40 p-[16px] md:border-0 md:p-0"
    >
      <p class="text-[14px] leading-[20px] text-white md:hidden">
        Seus dados
      </p>

      <UserV2FieldCard
        label="Nome de usuário"
        :value="userName || 'Não informado'"
      />
      <UserV2FieldCard
        v-if="editingField !== 'email'"
        icon="edit"
        label="E-mail"
        :value="userEmail || 'Não informado'"
        @click="openEditor('email')"
      />
      <div
        v-else
        class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
      >
        <UserAccountEmail
          ref="editorRef"
          :is-email-update-enabled="isEmailUpdateEnabled"
        />
      </div>
      <UserV2FieldCard
        v-if="editingField !== 'phone'"
        icon="edit"
        label="Celular"
        :value="phoneDisplay"
        @click="openEditor('phone')"
      />
      <div
        v-else
        class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
      >
        <UserAccountPhone
          ref="editorRef"
          :cancel-label="$t('general.back')"
        />
      </div>
      <UserV2FieldCard
        v-if="editingField !== 'address'"
        icon="edit"
        label="Endereço"
        :value="addressDisplay"
        @click="openEditor('address')"
      />
      <div
        v-else
        class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
      >
        <UserAccountAddress
          ref="editorRef"
          :cancel-label="$t('general.back')"
        />
      </div>
    </section>

    <!-- Documentos -->
    <section
      class="border-solid flex flex-col gap-[10px] rounded-[16px] border border-v2-default/40 p-[16px] md:border-0 md:p-0"
    >
      <p class="text-[14px] leading-[20px] text-white md:hidden">
        Documentos
      </p>

      <UserV2FieldCard
        v-if="editingField !== 'document'"
        icon="eye"
        label="Documento*"
        :value="documentDisplay"
        @click="openEditor('document')"
      />
      <div
        v-else
        class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
      >
        <UserAccountDocument ref="editorRef" />
      </div>
      <!-- Contratos: só com a seção habilitada (paridade com a antiga) -->
      <template v-if="isContractsSectionEnabled">
        <UserV2FieldCard
          v-if="editingField !== 'contracts'"
          icon="eye"
          label="Contratos e Aceitos"
          value="Termos e condições, Política de Privacidade, e mais..."
          @click="openEditor('contracts')"
        />
        <div
          v-else
          class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
        >
          <UserAccountContracts
            ref="editorRef"
            :cancel-label="$t('general.back')"
          />
        </div>
      </template>
      <!-- Conta bancária por moeda/flag (paridade com a página antiga):
             MXN → CLABE; BRL c/ bank_account_active → contas; senão → Pix -->
      <template v-if="userCurrency === 'MXN'">
        <UserV2FieldCard
          v-if="editingField !== 'clabe'"
          icon="edit"
          label="Conta bancária"
          value="Gerenciar sua CLABE"
          @click="openEditor('clabe')"
        />
        <div
          v-else
          class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
        >
          <UserAccountBankAccountsMex ref="editorRef" />
        </div>
      </template>
      <template v-else-if="userCurrency === 'BRL' && isBankAccountEnabled">
        <UserV2FieldCard
          v-if="editingField !== 'bank-accounts'"
          icon="edit"
          label="Conta bancária"
          value="Gerenciar suas contas bancárias"
          @click="openEditor('bank-accounts')"
        />
        <div
          v-else
          class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
        >
          <UserAccountBankAccounts ref="editorRef" />
        </div>
      </template>
      <template v-else>
        <UserV2FieldCard
          v-if="editingField !== 'pix'"
          icon="edit"
          label="Meu Pix"
          :value="pixDisplay"
          @click="openEditor('pix')"
        />
        <div
          v-else
          class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
        >
          <UserAccountPix
            ref="editorRef"
            :cancel-label="$t('general.back')"
          />
        </div>
      </template>
      <UserV2FieldCard
        v-if="editingField !== 'infos'"
        icon="edit"
        label="Dados da conta"
        :value="accountInfoDisplay"
        @click="openEditor('infos')"
      />
      <div
        v-else
        class="border-solid user-v2-editor relative rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 p-[12px]"
      >
        <UserAccountInfos
          ref="editorRef"
          :cancel-label="$t('general.back')"
        />
      </div>
      <!-- lista (não UserV2FieldCard): pode vir mais de uma nacionalidade,
           por isso o cartão-pai ganha uma borda separando cada uma -->
      <div
        class="border-solid flex w-full flex-col gap-[8px] rounded-[12px] border-2 border-v2-default-500 bg-v2-default-300 px-[12px] py-[8px]"
      >
        <p class="text-[12px] leading-[16px] text-v2-default-700">
          Nacionalidade (s)
        </p>
        <p
          v-for="nationality in visibleNationalitiesList"
          :key="nationality"
          class="border-solid rounded-[8px] border border-v2-blue-light/60 bg-v2-primary-100/40 px-[10px] py-[6px] text-[14px] leading-[20px] text-v2-foreground"
        >
          {{ nationality }}
        </p>
        <button
          v-if="userNationalitiesList.length > NATIONALITIES_COLLAPSED_LIMIT"
          class="self-start text-[12px] font-semibold text-v2-blue-light"
          type="button"
          @click="showAllNationalities = !showAllNationalities"
        >
          {{ showAllNationalities ? 'Ver menos' : 'Ver mais' }}
        </button>
      </div>
    </section>

    <!-- Verificações obrigatórias (some quando o usuário já está liberado
           pra saque, mesma regra do WithdrawAlert antigo) -->
    <div
      v-if="!isVerifiedToWithdraw"
      class="flex w-full flex-col gap-[10px] rounded-[16px] bg-v2-warning/20 p-[16px]"
    >
      <p class="text-[14px] leading-[20px] text-v2-warning">
        Verificações obrigatórias para solicitar saques acima de
        <b class="font-bold">{{ withdrawMinValue }}:</b>
      </p>
      <div class="flex w-full items-center justify-between">
        <div
          v-for="check in withdrawChecks"
          :key="check.label"
          class="flex items-center gap-[4px]"
        >
          <!-- tick-circle / close-circle (vuesax bold) -->
          <svg
            v-if="check.done"
            class="size-[20px] shrink-0 text-v2-success"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              fill="currentColor"
              r="10"
            />
            <path
              d="m8.5 12 2.4 2.4 4.6-4.8"
              stroke="#fff"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.8"
            />
          </svg>
          <svg
            v-else
            class="size-[20px] shrink-0 text-v2-danger"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              fill="currentColor"
              r="10"
            />
            <path
              d="m9 9 6 6m0-6-6 6"
              stroke="#fff"
              stroke-linecap="round"
              stroke-width="1.8"
            />
          </svg>
          <p class="text-[12px] leading-[16px] text-v2-warning">
            {{ check.label }}
          </p>
        </div>
      </div>
    </div>

    <!-- Zona de risco -->
    <div
      class="border-solid flex w-full flex-col gap-[10px] rounded-[16px] border border-v2-danger/20 p-[16px]"
    >
      <p class="text-[14px] leading-[20px] text-v2-danger">Zona de risco</p>
      <button
        class="flex h-[40px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-v2-danger/20 px-[16px] text-[14px] leading-[20px] text-v2-danger"
        type="button"
        @click="onDeleteAccount"
      >
        <UserV2Icon name="user-block" />
        Excluir conta
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Os editores embutidos reaproveitam os componentes da conta antiga
// (components/user/account/*), cujos inputs vêm no visual legado — fundo
// cinza (#424344 = auth-bg-inputs) e botão azul (button-bg). Aqui realinhamos
// o VISUAL pro padrão Minha Conta V2; lógica/validação/API dos componentes
// antigos continuam intactas. Exceção pontual (intencional, afeta os dois
// fluxos): Address.vue/Phone.vue tiveram o CTA "clique aqui" trocado por
// texto estático quando isEditing=true — evita o link redundante de abrir o
// editor flutuando por cima do próprio editor já aberto.
.user-v2-editor {
  // Caixa do input: classe base compartilhada por todos os inputs
  // (BaseFormInput e derivados). Cinza legado -> azul escuro do painel.
  :deep(.input-group) {
    background-color: rgb(var(--v2-default-400)) !important;
    border: 1px solid rgb(var(--v2-default-500)) !important;
    border-radius: 10px;
  }

  // Popovers/dropdowns que usam o fundo cinza direto na classe utilitária
  // (seletor de DDI do celular, selects de país/moeda/idioma).
  :deep(.bg-auth-bg-inputs) {
    background-color: rgb(var(--v2-default-300)) !important;
  }

  // Separador interno do DDI acompanha a borda v2.
  :deep(.ddi) {
    border-color: rgb(var(--v2-default-500)) !important;
  }

  // Botões de salvar/alterar/verificar: azul legado -> laranja (mesmo CTA
  // do "Depositar"). O botão do dropdown de DDI é type="button", então não
  // é atingido pelo seletor de submit.
  :deep(.col-span-2 button),
  :deep(form button[type='submit']) {
    background: rgb(var(--v2-secondary)) !important;
    color: rgb(var(--v2-secondary-foreground)) !important;
    border-radius: 8px !important;
    height: 40px;
  }
}
</style>
